// Single source of truth for the Zero-to-Hero scorecard dimensions: the
// interviewer gathers evidence per dimension, coverage decides when an
// interview can auto-submit, and the labels speak to founders in plain words.

export const Z2H_DIMENSIONS = [
  'founder_team',
  'problem_market',
  'traction_validation',
  'business_model',
  'financial_readiness',
  'tech_feasibility',
  'capital_fit',
  'commitment',
] as const

export type Z2hDimension = (typeof Z2H_DIMENSIONS)[number]

// Founder-facing phrasing — used in resume prompts and "here's what's left"
// guidance, never internal ids.
export const DIMENSION_LABELS: Record<Z2hDimension, string> = {
  founder_team: 'you and your team',
  problem_market: 'the problem and market',
  traction_validation: 'traction and validation',
  business_model: 'the business model',
  financial_readiness: 'financial readiness',
  tech_feasibility: 'the technical build',
  capital_fit: 'capital fit',
  commitment: 'commitment and timing',
}

type Answers = Record<string, Record<string, unknown>> | null | undefined

// Dimensions that still have no recorded evidence.
export function remainingDimensions(answers: Answers): Z2hDimension[] {
  return Z2H_DIMENSIONS.filter(
    (d) => !answers?.[d] || Object.keys(answers[d]).length === 0,
  )
}

export function labelsFor(dimensions: string[]): string[] {
  return dimensions.map(
    (d) => DIMENSION_LABELS[d as Z2hDimension] || d.replace(/_/g, ' '),
  )
}

// "the business model and capital fit" / "traction, capital fit, and commitment"
export function listLabels(dimensions: string[]): string {
  const labels = labelsFor(dimensions)
  if (labels.length <= 1) return labels[0] || ''
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}
